<template>
  <div class="ai-navigation__card-header">
    <div class="ai-navigation__card-meta">
      <span class="ai-navigation__card-badge" :class="`ai-navigation__card-badge--${item.post_type}`">
        {{ postTypeLabel }}
      </span>
      <span
        v-if="matchLevel.key !== 'normal'"
        class="ai-navigation__card-match"
        :class="`ai-navigation__card-match--${matchLevel.key}`"
      >{{ matchLevel.label }}</span>
    </div>
    <span v-if="showCta" class="ai-navigation__card-separator" aria-hidden="true"></span>
    <div v-if="showCta" class="ai-navigation__card-cta">
      <a :href="item.url" class="ai-navigation__card-cta-primary" draggable="false">
        {{ ctaLabel }}
      </a>
      <a
        v-if="item.post_type === 'product'"
        :href="`${item.url}#product-contact`"
        class="ai-navigation__card-cta-secondary"
        draggable="false"
      >お問い合わせ</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  getPostTypeLabel,
  getCtaLabel as getCtaLabelUtil,
  getMatchLevel as getMatchLevelUtil,
} from './useCardUtils';
import type { SearchItem } from './useCardUtils';

interface Props {
  item: SearchItem;
  showCta?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showCta: true,
});

const postTypeLabel = computed(() => getPostTypeLabel(props.item.post_type));
const ctaLabel = computed(() => getCtaLabelUtil(props.item.post_type));
const matchLevel = computed(() => getMatchLevelUtil(props.item));
</script>

<style scoped>
.ai-navigation__card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ai-navigation__card-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ai-navigation__card-badge {
  font-size: 16px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  letter-spacing: .04em;
}

.ai-navigation__card-badge--genre   { background: #1a73e8; color: #fff; }
.ai-navigation__card-badge--product { background: #34a853; color: #fff; }
.ai-navigation__card-badge--column  { background: #fbbc04; color: #5a4500; }
.ai-navigation__card-badge--feature { background: #9c27b0; color: #fff; }

.ai-navigation__card-match {
  font-size: 16px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  letter-spacing: .04em;
}

.ai-navigation__card-match--best {
  background: linear-gradient(135deg, #F47521 0%, #D85E0F 100%);
  color: #fff;
}

.ai-navigation__card-match--recommend {
  background: #EAF9FA;
  color: #00A0E9;
  border: 1px solid rgba(0, 160, 233, .3);
}

.ai-navigation__card-cta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ai-navigation__card-cta-primary {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
  color: #00A0E9;
  text-decoration: none;
  padding: 5px 12px;
  border-radius: 6px;
  background: #EAF9FA;
  border: 1px solid rgba(0, 160, 233, .25);
  transition: all .2s ease;
  white-space: nowrap;
}

.ai-navigation__card-cta-primary:hover {
  background: #00A0E9;
  color: #fff;
  border-color: transparent;
}

.ai-navigation__card-cta-secondary {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #F47521;
  text-decoration: none;
  padding: 5px 12px;
  border-radius: 6px;
  background: #FFF5EE;
  border: 1px solid rgba(244, 117, 33, .25);
  transition: all .2s ease;
  white-space: nowrap;
}

.ai-navigation__card-cta-secondary:hover {
  background: linear-gradient(135deg, #F47521 0%, #D85E0F 100%);
  color: #fff;
  border-color: transparent;
}

.ai-navigation__card-separator {
  width: 1px;
  align-self: stretch;
  min-height: 16px;
  background: #d0d0d0;
  flex-shrink: 0;
}
</style>
